import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
  ArrowLeftIcon,
  PlusIcon,
  DocumentTextIcon,
  CodeBracketIcon,
  LightBulbIcon,
  BookOpenIcon,
  BuildingOfficeIcon,
  TrashIcon,
  PencilIcon
} from '@heroicons/react/24/outline'
import { RootState } from '../../store'
import toast from 'react-hot-toast'

interface Problem {
  id: string
  title: string
  description: string
  difficulty: 'easy' | 'medium' | 'hard'
  timeLimit: number
  points: number
  testCases?: Array<{
    input: string
    expected_output: string
    is_hidden: boolean
  }>
  options?: Array<{
    id: string
    text: string
    isCorrect: boolean
  }>
  correctAnswer?: string
  explanation?: string
}

interface Assessment {
  id: string
  title: string
  description: string
  category: {
    collegeType: 'engineering' | 'management'
    tier: 'tier1' | 'tier2'
  }
  assessmentType: 'coding' | 'aptitude' | 'fundamentals' | 'case-study'
  duration: number
  totalQuestions: number
  totalPoints: number
  problems: Problem[]
}

const CreateAssessmentPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useSelector((state: RootState) => state.auth)

  const [assessment, setAssessment] = useState<Assessment>({
    id: '',
    title: '',
    description: '',
    category: { collegeType: 'engineering', tier: 'tier1' },
    assessmentType: 'coding',
    duration: 60,
    totalQuestions: 0,
    totalPoints: 0,
    problems: []
  })

  const [showProblemModal, setShowProblemModal] = useState(false)
  const [editingProblem, setEditingProblem] = useState<Problem | null>(null)
  const [currentProblem, setCurrentProblem] = useState<Partial<Problem>>({
    title: '',
    description: '',
    difficulty: 'medium',
    timeLimit: 30,
    points: 10
  })

  useEffect(() => {
    // Get category and assessment type from navigation state
    if (location.state?.category && location.state?.assessmentType) {
      setAssessment(prev => ({
        ...prev,
        category: location.state.category,
        assessmentType: location.state.assessmentType
      }))
    }
  }, [location.state])

  const getAssessmentTypeInfo = () => {
    const { assessmentType, category } = assessment
    
    if (assessmentType === 'coding') {
      return {
        title: 'Coding Problems',
        icon: CodeBracketIcon,
        color: 'blue',
        description: 'Create algorithmic and programming problems'
      }
    } else if (assessmentType === 'aptitude') {
      return {
        title: 'Aptitude & Reasoning',
        icon: LightBulbIcon,
        color: 'green',
        description: 'Create logical reasoning and aptitude questions'
      }
    } else if (assessmentType === 'case-study') {
      return {
        title: 'Case-study & Business Simulations',
        icon: BuildingOfficeIcon,
        color: 'blue',
        description: 'Create business case studies and simulations'
      }
    } else {
      return {
        title: 'Subject Fundamentals',
        icon: BookOpenIcon,
        color: 'purple',
        description: 'Create subject-specific fundamental questions'
      }
    }
  }

  const handleAddProblem = () => {
    setCurrentProblem({
      title: '',
      description: '',
      difficulty: 'medium',
      timeLimit: 30,
      points: 10
    })
    setEditingProblem(null)
    setShowProblemModal(true)
  }

  const handleEditProblem = (problem: Problem) => {
    setCurrentProblem(problem)
    setEditingProblem(problem)
    setShowProblemModal(true)
  }

  const handleDeleteProblem = (problemId: string) => {
    setAssessment(prev => ({
      ...prev,
      problems: prev.problems.filter(p => p.id !== problemId),
      totalQuestions: prev.totalQuestions - 1,
      totalPoints: prev.totalPoints - (prev.problems.find(p => p.id === problemId)?.points || 0)
    }))
    toast.success('Problem deleted successfully')
  }

  const handleSaveProblem = () => {
    if (!currentProblem.title || !currentProblem.description) {
      toast.error('Please fill in all required fields')
      return
    }

    // Validate test cases for coding questions
    if (assessment.assessmentType === 'coding' && (!currentProblem.testCases || currentProblem.testCases.length === 0)) {
      toast.error('Please add at least one test case for coding questions')
      return
    }

    const problem: Problem = {
      id: editingProblem?.id || `problem_${Date.now()}`,
      title: currentProblem.title,
      description: currentProblem.description,
      difficulty: currentProblem.difficulty || 'medium',
      timeLimit: currentProblem.timeLimit || 30,
      points: currentProblem.points || 10,
      testCases: currentProblem.testCases || [],
      options: currentProblem.options || [],
      correctAnswer: currentProblem.correctAnswer || '',
      explanation: currentProblem.explanation || ''
    }

    if (editingProblem) {
      // Update existing problem
      setAssessment(prev => ({
        ...prev,
        problems: prev.problems.map(p => p.id === problem.id ? problem : p),
        totalPoints: prev.totalPoints - editingProblem.points + problem.points
      }))
      toast.success('Problem updated successfully')
    } else {
      // Add new problem
      setAssessment(prev => ({
        ...prev,
        problems: [...prev.problems, problem],
        totalQuestions: prev.totalQuestions + 1,
        totalPoints: prev.totalPoints + problem.points
      }))
      toast.success('Problem added successfully')
    }

    setShowProblemModal(false)
    setCurrentProblem({})
    setEditingProblem(null)
  }

  const handleSaveAssessment = async () => {
    if (!assessment.title || !assessment.description || assessment.problems.length === 0) {
      toast.error('Please fill in all required fields and add at least one problem')
      return
    }

    try {
      const newAssessment = {
        ...assessment,
        id: `assessment_${Date.now()}`,
        createdAt: new Date().toISOString()
      }
      
      // Save assessment to localStorage
      const savedAssessments = localStorage.getItem('createdAssessments')
      const assessments = savedAssessments ? JSON.parse(savedAssessments) : []
      assessments.push(newAssessment)
      localStorage.setItem('createdAssessments', JSON.stringify(assessments))
      
      // Sync assessment data to backend using relative URL for IIS proxy
      try {
        const response = await fetch('/api/v1/assessments/sync', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(newAssessment)
        })
        
        if (response.ok) {
          console.log('Assessment synced to backend successfully')
        } else {
          console.warn('Failed to sync assessment to backend, but saved locally')
        }
      } catch (syncError) {
        console.warn('Failed to sync assessment to backend:', syncError)
        // Don't fail the whole operation if sync fails
      }
      
      console.log('Assessment saved:', newAssessment)
      toast.success('Assessment created successfully!')
      navigate('/dashboard')
    } catch (error) {
      console.error('Failed to save assessment:', error)
      toast.error('Failed to save assessment')
    }
  }

  const assessmentTypeInfo = getAssessmentTypeInfo()
  const IconComponent = assessmentTypeInfo.icon

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors mr-4"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              Back to Dashboard
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Create Assessment
              </h1>
              <div className="flex items-center mt-2">
                <div className={`p-2 bg-${assessmentTypeInfo.color}-100 rounded-lg mr-3`}>
                  <IconComponent className={`w-6 h-6 text-${assessmentTypeInfo.color}-600`} />
                </div>
                <div>
                  <p className="text-gray-600">
                    {assessmentTypeInfo.title} - {assessment.category.collegeType === 'engineering' ? 'Engineering' : 'Management'} {assessment.category.tier === 'tier1' ? 'Tier 1' : 'Tier 2'}
                  </p>
                  <p className="text-sm text-gray-500">{assessmentTypeInfo.description}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Assessment Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Basic Information</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assessment Title *
                  </label>
                  <input
                    type="text"
                    value={assessment.title}
                    onChange={(e) => setAssessment(prev => ({ ...prev, title: e.target.value }))}
                    className="input w-full"
                    placeholder="Enter assessment title"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    value={assessment.description}
                    onChange={(e) => setAssessment(prev => ({ ...prev, description: e.target.value }))}
                    className="input w-full h-24"
                    placeholder="Enter assessment description"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Duration (minutes) *
                    </label>
                    <input
                      type="number"
                      value={assessment.duration}
                      onChange={(e) => setAssessment(prev => ({ ...prev, duration: parseInt(e.target.value) || 60 }))}
                      className="input w-full"
                      min="1"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Problems/Questions */}
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Problems/Questions</h2>
                <button
                  onClick={handleAddProblem}
                  className="btn btn-primary flex items-center space-x-2"
                >
                  <PlusIcon className="w-4 h-4" />
                  <span>Add Problem</span>
                </button>
              </div>
              
              {assessment.problems.length === 0 ? (
                <div className="text-center py-8">
                  <DocumentTextIcon className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No problems added yet</p>
                  <p className="text-sm text-gray-400">Click "Add Problem" to get started</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {assessment.problems.map((problem, index) => (
                    <div key={problem.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                            <h3 className="font-medium text-gray-900">{problem.title}</h3>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              problem.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                              problem.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {problem.difficulty}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{problem.description}</p>
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span>{problem.timeLimit} min</span>
                            <span>{problem.points} points</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEditProblem(problem)}
                            className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteProblem(problem.id)}
                            className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Assessment Summary */}
          <div className="space-y-6">
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Assessment Summary</h3>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Questions:</span>
                  <span className="font-medium">{assessment.totalQuestions}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Points:</span>
                  <span className="font-medium">{assessment.totalPoints}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Duration:</span>
                  <span className="font-medium">{assessment.duration} min</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Category:</span>
                  <span className="font-medium capitalize">
                    {assessment.category.collegeType} - {assessment.category.tier === 'tier1' ? 'Tier 1' : 'Tier 2'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Type:</span>
                  <span className="font-medium">{assessmentTypeInfo.title}</span>
                </div>
              </div>
              
              <div className="mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={handleSaveAssessment}
                  className="btn btn-primary w-full"
                  disabled={!assessment.title || !assessment.description || assessment.problems.length === 0}
                >
                  Create Assessment
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Problem Creation/Edit Modal */}
      {showProblemModal && (
        <ProblemModal
          problem={currentProblem}
          setProblem={setCurrentProblem}
          assessmentType={assessment.assessmentType}
          onSave={handleSaveProblem}
          onCancel={() => {
            setShowProblemModal(false)
            setCurrentProblem({})
            setEditingProblem(null)
          }}
          isEditing={!!editingProblem}
        />
      )}
    </div>
  )
}

// Problem Modal Component
interface ProblemModalProps {
  problem: Partial<Problem>
  setProblem: (problem: Partial<Problem>) => void
  assessmentType: string
  onSave: () => void
  onCancel: () => void
  isEditing: boolean
}

const ProblemModal: React.FC<ProblemModalProps> = ({
  problem,
  setProblem,
  assessmentType,
  onSave,
  onCancel,
  isEditing
}) => {
  const [newTestCase, setNewTestCase] = useState({ input: '', expected_output: '', is_hidden: false })
  const [newOption, setNewOption] = useState({ text: '', isCorrect: false })

  const addTestCase = () => {
    if (newTestCase.input && newTestCase.expected_output) {
      setProblem({
        ...problem,
        testCases: [...(problem.testCases || []), { ...newTestCase }]
      })
      setNewTestCase({ input: '', expected_output: '', is_hidden: false })
    }
  }

  const removeTestCase = (index: number) => {
    setProblem({
      ...problem,
      testCases: problem.testCases?.filter((_, i) => i !== index)
    })
  }

  const addOption = () => {
    if (newOption.text) {
      setProblem({
        ...problem,
        options: [...(problem.options || []), { ...newOption, id: `opt_${Date.now()}` }]
      })
      setNewOption({ text: '', isCorrect: false })
    }
  }

  const removeOption = (index: number) => {
    setProblem({
      ...problem,
      options: problem.options?.filter((_, i) => i !== index)
    })
  }

  const updateOption = (index: number, field: string, value: any) => {
    const updatedOptions = problem.options?.map((opt, i) => 
      i === index ? { ...opt, [field]: value } : opt
    )
    setProblem({ ...problem, options: updatedOptions })
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900">
            {isEditing ? 'Edit Problem' : 'Add New Problem'}
          </h3>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-6">
          {/* Basic Problem Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Problem Title *
              </label>
              <input
                type="text"
                value={problem.title || ''}
                onChange={(e) => setProblem({ ...problem, title: e.target.value })}
                className="input w-full"
                placeholder="Enter problem title"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Difficulty *
              </label>
              <select
                value={problem.difficulty || 'medium'}
                onChange={(e) => setProblem({ ...problem, difficulty: e.target.value as any })}
                className="input w-full"
              >
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Time Limit (minutes) *
              </label>
              <input
                type="number"
                value={problem.timeLimit || 30}
                onChange={(e) => setProblem({ ...problem, timeLimit: parseInt(e.target.value) || 30 })}
                className="input w-full"
                min="1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Points *
              </label>
              <input
                type="number"
                value={problem.points || 10}
                onChange={(e) => setProblem({ ...problem, points: parseInt(e.target.value) || 10 })}
                className="input w-full"
                min="1"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Problem Description *
            </label>
            <textarea
              value={problem.description || ''}
              onChange={(e) => setProblem({ ...problem, description: e.target.value })}
              className="input w-full h-32"
              placeholder="Enter problem description"
            />
          </div>

          {/* Assessment Type Specific Fields */}
          {assessmentType === 'coding' && (
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-3">Test Cases</h4>
              <div className="space-y-3">
                {problem.testCases?.map((testCase, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Test Case {index + 1}</span>
                      <div className="flex items-center space-x-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={testCase.is_hidden}
                            onChange={(e) => {
                              const updatedTestCases = problem.testCases?.map((tc, i) => 
                                i === index ? { ...tc, is_hidden: e.target.checked } : tc
                              )
                              setProblem({ ...problem, testCases: updatedTestCases })
                            }}
                            className="mr-1"
                          />
                          <span className="text-xs">Hidden</span>
                        </label>
                        <button
                          onClick={() => removeTestCase(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Input</label>
                        <textarea
                          value={testCase.input}
                          onChange={(e) => {
                            const updatedTestCases = problem.testCases?.map((tc, i) => 
                              i === index ? { ...tc, input: e.target.value } : tc
                            )
                            setProblem({ ...problem, testCases: updatedTestCases })
                          }}
                          className="input w-full h-20 text-sm"
                          placeholder="Test input"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Expected Output</label>
                        <textarea
                          value={testCase.expected_output}
                          onChange={(e) => {
                            const updatedTestCases = problem.testCases?.map((tc, i) => 
                              i === index ? { ...tc, expected_output: e.target.value } : tc
                            )
                            setProblem({ ...problem, testCases: updatedTestCases })
                          }}
                          className="input w-full h-20 text-sm"
                          placeholder="Expected output"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  <h5 className="text-sm font-medium text-gray-900 mb-2">Add New Test Case</h5>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Input</label>
                      <textarea
                        value={newTestCase.input}
                        onChange={(e) => setNewTestCase({ ...newTestCase, input: e.target.value })}
                        className="input w-full h-20 text-sm"
                        placeholder="Test input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Expected Output</label>
                      <textarea
                        value={newTestCase.expected_output}
                        onChange={(e) => setNewTestCase({ ...newTestCase, expected_output: e.target.value })}
                        className="input w-full h-20 text-sm"
                        placeholder="Expected output"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newTestCase.is_hidden}
                        onChange={(e) => setNewTestCase({ ...newTestCase, is_hidden: e.target.checked })}
                        className="mr-1"
                      />
                      <span className="text-xs">Hidden test case</span>
                    </label>
                    <button
                      onClick={addTestCase}
                      className="btn btn-outline btn-sm"
                    >
                      Add Test Case
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {(assessmentType === 'aptitude' || assessmentType === 'fundamentals') && (
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-3">Multiple Choice Options</h4>
              <div className="space-y-3">
                {problem.options?.map((option, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg">
                    <input
                      type="radio"
                      name="correctAnswer"
                      checked={option.isCorrect}
                      onChange={() => {
                        const updatedOptions = problem.options?.map((opt, i) => 
                          i === index ? { ...opt, isCorrect: true } : { ...opt, isCorrect: false }
                        )
                        setProblem({ ...problem, options: updatedOptions })
                      }}
                    />
                    <input
                      type="text"
                      value={option.text}
                      onChange={(e) => updateOption(index, 'text', e.target.value)}
                      className="input flex-1"
                      placeholder="Option text"
                    />
                    <button
                      onClick={() => removeOption(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  <h5 className="text-sm font-medium text-gray-900 mb-2">Add New Option</h5>
                  <div className="flex items-center space-x-3">
                    <input
                      type="text"
                      value={newOption.text}
                      onChange={(e) => setNewOption({ ...newOption, text: e.target.value })}
                      className="input flex-1"
                      placeholder="Option text"
                    />
                    <button
                      onClick={addOption}
                      className="btn btn-outline btn-sm"
                    >
                      Add Option
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {assessmentType === 'case-study' && (
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-3">Case Study Details</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Correct Answer/Expected Response
                  </label>
                  <textarea
                    value={problem.correctAnswer || ''}
                    onChange={(e) => setProblem({ ...problem, correctAnswer: e.target.value })}
                    className="input w-full h-24"
                    placeholder="Describe the expected answer or key points"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Explanation/Solution
                  </label>
                  <textarea
                    value={problem.explanation || ''}
                    onChange={(e) => setProblem({ ...problem, explanation: e.target.value })}
                    className="input w-full h-24"
                    placeholder="Provide detailed explanation or solution approach"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Common Fields */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Explanation/Solution (Optional)
            </label>
            <textarea
              value={problem.explanation || ''}
              onChange={(e) => setProblem({ ...problem, explanation: e.target.value })}
              className="input w-full h-24"
              placeholder="Provide explanation or solution approach"
            />
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
          <button
            onClick={onCancel}
            className="btn btn-outline"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="btn btn-primary"
          >
            {isEditing ? 'Update Problem' : 'Add Problem'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CreateAssessmentPage
